import {
    CURRENT_APP_GROWAVE,
    CURRENT_APP_QUERY_PARAMETER,
} from "@/constants/current_app";
import {
    globalLoggerToken,
    gwStorefrontAppInfoToken,
    localizationInfoProviderToken,
} from "@/tokens";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import { SDK_MODULES } from "@modules/sdk/constants";
import { subscribeToSdkMethod } from "@modules/sdk/helpers";
import { SdkModule } from "@modules/sdk/SdkModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import { container, instanceCachingFactory, singleton } from "tsyringe";

import { ModuleInterface } from "@interfaces/ModuleInterface";

import { GrowaveProductApiService } from "./api_services/GrowaveProductApiService";
import { GrowaveProductLiquidApiService } from "./api_services/GrowaveProductLiquidApiService";
import { GrowaveUnavailableProductsApiService } from "./api_services/GrowaveUnavailableProductsApiService";
import { ShopifyGraphQLProductsApiService } from "./api_services/ShopifyGraphQLProductsApiService";
import { PRODUCTS_SDK_METHODS } from "./products_sdk/constants";
import GetProductsSharedStateKitSdkMethodPayload from "./products_sdk/methods/GetProductsSharedStateKitSdkMethodPayload";
import { ProductsSharedStateKit } from "./products_sdk/state_kit/ProductsSharedStateKit";
import { CombinedProductsAndProductsWithQuantityRulesQuery } from "./queries/CombinedProductsAndProductsWithQuantityRulesQuery";
import { ProductsQuery } from "./queries/ProductsQuery";
import { ProductsWithQuantityRulesQuery } from "./queries/ProductsWithQuantityRulesQuery";
import { UnavailableProductsQuery } from "./queries/UnavailableProductsQuery";
import { ProductsService } from "./services/ProductsService";
import { UnavailableProductsService } from "./services/UnavailableProductsService";
import { UnavailableProductsServiceInterface } from "./services/UnavailableProductsServiceInterface";
import { ProductsStore } from "./stores/ProductsStore";
import { ProductsWithQuantityRulesStore } from "./stores/ProductsWithQuantityRulesStore";
import { UnavailableProductsStore } from "./stores/UnavailableProductsStore";
import {
    combinedProductsAndProductsWithQuantityRulesQueryToken,
    commonAuthApiClientToken,
    productApiServiceToken,
    productLiquidApiServiceToken,
    productsApiServiceToken,
    productsQueryToken,
    productsServiceToken,
    productsStoreToken,
    productsWithQuantityRulesQueryToken,
    productsWithQuantityRulesStoreToken,
    unavailableProductsApiServiceToken,
    unavailableProductsQueryToken,
    unavailableProductsServiceToken,
    unavailableProductsStoreToken,
} from "./tokens";

interface ProductsModuleExports {
    productsQuery: ProductsQuery;
    productsService: ProductsServiceInterface;
    unavailableProductsQuery: UnavailableProductsQuery;
    unavailableProductsService: UnavailableProductsServiceInterface;
    combinedProductsAndProductsWithQuantityRulesQuery: CombinedProductsAndProductsWithQuantityRulesQuery;
}

@singleton()
export class ProductsModule implements ModuleInterface<ProductsModuleExports> {
    public exports: ProductsModuleExports;

    public constructor(
        private readonly tokenManagerModule: TokenManagerModule,
        private readonly analyticsEventsCollectorModule: AnalyticsEventsCollectorModule,
        private readonly commonModule: CommonModule
    ) {
        container.register(commonAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/common-storefront/v2"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(globalLoggerToken)
                        ),
                        this.analyticsEventsCollectorModule.exports.eventsCollector,
                        this.commonModule.exports.locationController,
                        this.analyticsEventsCollectorModule.exports.eventsFactory
                    ),
                    this.tokenManagerModule.exports.tokenManager
                );
            }),
        });
        container.register(productApiServiceToken, {
            useFactory: instanceCachingFactory(() => {
                const gwStorefrontAppInfo = container.resolve(
                    gwStorefrontAppInfoToken
                );
                if (gwStorefrontAppInfo.appName === CURRENT_APP_GROWAVE) {
                    return new GrowaveProductApiService(
                        container.resolve(commonAuthApiClientToken)
                    );
                }
                return container.resolve(productLiquidApiServiceToken);
            }),
        });
        container.register(unavailableProductsApiServiceToken, {
            useFactory: instanceCachingFactory(() => {
                return new GrowaveUnavailableProductsApiService(
                    container.resolve(commonAuthApiClientToken)
                );
            }),
        });
        container.register(productLiquidApiServiceToken, {
            useFactory: instanceCachingFactory(() => {
                return new GrowaveProductLiquidApiService(
                    container.resolve(commonAuthApiClientToken)
                );
            }),
        });
        container.register(productsApiServiceToken, {
            useFactory: instanceCachingFactory(() => {
                const localizationInfoProvider = container.resolve(
                    localizationInfoProviderToken
                );
                if (this.commonModule.exports.graphqlApiClient) {
                    return new ShopifyGraphQLProductsApiService(
                        this.commonModule.exports.graphqlApiClient,
                        localizationInfoProvider.getCountryIsoCode(),
                        localizationInfoProvider.getLanguageIsoCode()
                    );
                }
            }),
        });
        container.register(productsStoreToken, {
            useFactory: instanceCachingFactory(() => {
                return new ProductsStore();
            }),
        });
        container.register(unavailableProductsStoreToken, {
            useFactory: instanceCachingFactory(() => {
                return new UnavailableProductsStore();
            }),
        });
        container.register(productsWithQuantityRulesStoreToken, {
            useFactory: instanceCachingFactory(() => {
                return new ProductsWithQuantityRulesStore();
            }),
        });
        container.register(productsQueryToken, {
            useFactory: instanceCachingFactory(() => {
                return new ProductsQuery(container.resolve(productsStoreToken));
            }),
        });
        container.register(unavailableProductsQueryToken, {
            useFactory: instanceCachingFactory(() => {
                return new UnavailableProductsQuery(
                    container.resolve(unavailableProductsStoreToken)
                );
            }),
        });
        container.register(productsWithQuantityRulesQueryToken, {
            useFactory: instanceCachingFactory(() => {
                return new ProductsWithQuantityRulesQuery(
                    container.resolve(productsWithQuantityRulesStoreToken)
                );
            }),
        });
        container.register(
            combinedProductsAndProductsWithQuantityRulesQueryToken,
            {
                useFactory: instanceCachingFactory(() => {
                    return new CombinedProductsAndProductsWithQuantityRulesQuery(
                        container.resolve(productsStoreToken),
                        container.resolve(productsWithQuantityRulesQueryToken)
                    );
                }),
            }
        );
        container.register(productsServiceToken, {
            useFactory: instanceCachingFactory(() => {
                return new ProductsService(
                    container.resolve(productApiServiceToken),
                    container.resolve(productsApiServiceToken),
                    container.resolve(productLiquidApiServiceToken),
                    container.resolve(productsStoreToken),
                    container.resolve(productsWithQuantityRulesStoreToken),
                    container.resolve(globalLoggerToken)
                );
            }),
        });
        container.register(unavailableProductsServiceToken, {
            useFactory: instanceCachingFactory(() => {
                return new UnavailableProductsService(
                    container.resolve(unavailableProductsApiServiceToken),
                    container.resolve(unavailableProductsStoreToken),
                    container.resolve(globalLoggerToken)
                );
            }),
        });
        container.resolve(productsServiceToken).subscribe();
        container.resolve(unavailableProductsServiceToken).subscribe();

        // Register Products SDK
        void (async () => {
            const { sdk, sdkDynamicModulesManager, sdkMethodsCollector } =
                container.resolve(SdkModule).exports;

            const ProductsSdk = await sdkDynamicModulesManager.import(
                SDK_MODULES.PRODUCTS
            );
            const productsSdk = new ProductsSdk(sdkMethodsCollector);
            sdk.registerModule(SDK_MODULES.PRODUCTS, productsSdk);

            // Subscribe to SDK methods
            const productsQuery = container.resolve(productsQueryToken);
            const productsService = container.resolve(productsServiceToken);

            subscribeToSdkMethod<GetProductsSharedStateKitSdkMethodPayload>(
                PRODUCTS_SDK_METHODS.GET_PRODUCTS_SHARED_STATE_KIT,
                ({ onSuccess }) => {
                    const stateKit = new ProductsSharedStateKit(
                        productsQuery,
                        productsService
                    );

                    onSuccess(stateKit);
                }
            );
        })();

        this.exports = {
            productsQuery: container.resolve(productsQueryToken),
            productsService: container.resolve(productsServiceToken),
            unavailableProductsQuery: container.resolve(
                unavailableProductsQueryToken
            ),
            unavailableProductsService: container.resolve(
                unavailableProductsServiceToken
            ),
            combinedProductsAndProductsWithQuantityRulesQuery:
                container.resolve(
                    combinedProductsAndProductsWithQuantityRulesQueryToken
                ),
        };
    }

    registerProviders() {
        return {};
    }
}
